/**
 * Browse Designers Page
 * Search and filter interior designers
 */

import { BadgeCheck, CircleCheck, Clock, IndianRupee } from "lucide-react";
import type * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleChip } from "@/components/ui/toggle-chip";
import { CardRating } from "@/features/marketplace/components/card-rating";
import {
	browseDesigners,
	type DesignerProfile,
} from "@/features/marketplace/services/marketplace-api.service";
import { useCityFilterOptions } from "@/lib/constants/markets";
import { formatPrice } from "@/lib/format";
import { cn, resolveMediaUrl } from "@/lib/utils";

const DESIGN_STYLES = [
	"Contemporary",
	"Modern",
	"Traditional",
	"Minimalist",
	"Industrial",
	"Scandinavian",
];

const BUDGET_RANGES = [
	{ value: "", label: "Any Budget" },
	{ value: "500000", label: "Up to ₹5 Lakh" },
	{ value: "1500000", label: "Up to ₹15 Lakh" },
	{ value: "3000000", label: "Up to ₹30 Lakh" },
	{ value: "5000000", label: "Up to ₹50 Lakh" },
];

function DesignerCard({ designer }: { designer: DesignerProfile }) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<button
			type="button"
			className="w-full text-left group cursor-pointer rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[var(--dashboard-shadow-3)]"
			onClick={() => navigate(`/designers/${designer.id}`)}
		>
			<div className="flex items-start gap-4">
				{/* Avatar */}
				<div
					className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-xl font-display font-bold border border-dashboard-border shadow-[var(--dashboard-shadow-avatar)]"
					style={{
						background: designer.profilePictureUrl
							? "transparent"
							: "linear-gradient(135deg, var(--color-dashboard-warm-200), var(--color-dashboard-orange-300))",
						color: "var(--color-dashboard-orange-text-on-tint)",
					}}
				>
					{designer.profilePictureUrl ? (
						<img
							src={resolveMediaUrl(designer.profilePictureUrl)}
							alt={designer.name}
							className="h-16 w-16 rounded-full object-cover"
						/>
					) : (
						designer.name.charAt(0).toUpperCase()
					)}
				</div>

				{/* Info */}
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<h3 className="font-display text-[20px] font-medium text-dashboard-text group-hover:text-dashboard-accent-dark transition-colors truncate">
							{designer.name}
						</h3>
						{designer.isVerified && (
							<BadgeCheck className="h-5 w-5 text-dashboard-accent-dark shrink-0" />
						)}
					</div>

					{designer.firmName && (
						<p className="font-body text-xs text-dashboard-muted font-medium tracking-wide truncate">
							{designer.firmName}
						</p>
					)}

					{designer.bio && (
						<p className="mt-2.5 font-body text-[13px] text-dashboard-muted-dark leading-relaxed line-clamp-2 text-left">
							{designer.bio}
						</p>
					)}
				</div>
			</div>

			{/* Stats */}
			<div className="mt-5 flex flex-wrap items-center gap-4 border-t border-dashboard-divider pt-4 text-xs font-mono tracking-wider text-dashboard-muted">
				<CardRating
					averageRating={designer.averageRating}
					reviewCount={designer.reviewCount}
				/>

				{designer.experienceYears && (
					<span className="flex items-center gap-2">
						<Clock className="h-4 w-4 text-dashboard-accent-dark" />
						{designer.experienceYears} {t("common.yrsExp").toUpperCase()}
					</span>
				)}

				{designer.projectsCompleted && designer.projectsCompleted > 0 && (
					<span className="flex items-center gap-2">
						<CircleCheck className="h-4 w-4 text-dashboard-accent-dark" />
						{designer.projectsCompleted} {t("common.projects").toUpperCase()}
					</span>
				)}

				{designer.priceRangeMin && designer.priceRangeMax && (
					<span className="flex items-center gap-1.5 text-dashboard-text font-semibold">
						<IndianRupee className="h-4 w-4 text-dashboard-accent-dark" />
						{formatPrice(designer.priceRangeMin)} -{" "}
						{formatPrice(designer.priceRangeMax)}
					</span>
				)}
			</div>

			{/* Styles */}
			{designer.styles && designer.styles.length > 0 && (
				<div className="mt-4 flex flex-wrap gap-2">
					{designer.styles.slice(0, 3).map((style) => (
						<span
							key={style}
							className="rounded-full bg-dashboard-surface border border-dashboard-border/60 px-3 py-0.5 font-body text-[11px] font-medium text-dashboard-text"
						>
							{style}
						</span>
					))}
					{designer.styles.length > 3 && (
						<span className="rounded-full bg-dashboard-warm-100 border border-dashboard-border/40 px-2 py-0.5 font-body text-[11px] text-dashboard-muted">
							{t("common.plusMore", { count: designer.styles.length - 3 })}
						</span>
					)}
				</div>
			)}

			{/* Cities */}
			{designer.serviceCities && designer.serviceCities.length > 0 && (
				<div className="mt-3 font-mono text-[9px] uppercase tracking-widest text-dashboard-muted">
					{t("common.serves").toUpperCase()} {designer.serviceCities.join(", ")}
				</div>
			)}
		</button>
	);
}

export function BrowseDesignersPage() {
	const { t } = useTranslation();
	const CITIES = useCityFilterOptions();
	const [designers, setDesigners] = useState<DesignerProfile[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);

	// Filters
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedCity, setSelectedCity] = useState("");
	const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
	const [budgetMax, setBudgetMax] = useState("");

	const toggleStyle = (style: string) => {
		setSelectedStyles((prev) =>
			prev.includes(style) ? prev.filter((s) => s !== style) : [...prev, style],
		);
	};

	const fetchDesigners = useCallback(async () => {
		setIsLoading(true);
		try {
			const result = await browseDesigners({
				query: searchQuery || undefined,
				city: selectedCity || undefined,
				styles: selectedStyles.length > 0 ? selectedStyles : undefined,
				budgetMax: budgetMax ? parseInt(budgetMax, 10) : undefined,
				page,
				limit: 12,
			});
			setDesigners(result.designers);
			setTotal(result.total);
			setTotalPages(result.totalPages);
		} catch {
			console.error("Failed to fetch designers");
		} finally {
			setIsLoading(false);
		}
	}, [searchQuery, selectedCity, selectedStyles, budgetMax, page]);

	useEffect(() => {
		fetchDesigners();
	}, [fetchDesigners]);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setPage(1);
		fetchDesigners();
	};

	const clearFilters = () => {
		setSearchQuery("");
		setSelectedCity("");
		setSelectedStyles([]);
		setBudgetMax("");
		setPage(1);
	};

	const hasFilters =
		searchQuery || selectedCity || selectedStyles.length > 0 || budgetMax;

	return (
		<div
			className="min-h-screen pb-12"
			style={{
				background:
					"radial-gradient(ellipse at 0% 0%, color-mix(in srgb, var(--color-dashboard-accent-light) 6%, transparent) 0%, transparent 45%), radial-gradient(ellipse at 100% 100%, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 0%, transparent 50%), var(--color-dashboard-surface)",
			}}
		>
			{/* Header */}
			<div className="mx-auto max-w-7xl px-4 pt-10 pb-6">
				<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted">
					{t("common.marketplace")}
				</p>
				<h1 className="mt-2 font-display text-[42px] font-medium leading-[1.05] tracking-[-0.02em] text-dashboard-text sm:text-[48px] lg:text-[54px]">
					{t("browse.findDesigners")}
				</h1>
				<p className="mt-3 font-body text-base text-dashboard-muted max-w-2xl leading-relaxed">
					{t("browse.findDesignersSubtitle")}
				</p>
			</div>

			<div className="mx-auto max-w-7xl px-4 py-6">
				<div className="flex flex-col gap-6 lg:flex-row">
					{/* Filters Sidebar */}
					<div className="w-full lg:w-68 shrink-0">
						<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-5 shadow-[var(--dashboard-shadow-1)]">
							<div className="flex items-center justify-between mb-5 border-b border-dashboard-divider pb-3">
								<h2 className="font-mono text-xs font-bold uppercase tracking-widest text-dashboard-text">
									{t("common.filters")}
								</h2>
								{hasFilters && (
									<button
										type="button"
										onClick={clearFilters}
										className="font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-accent-dark hover:underline"
									>
										{t("common.clearAll")}
									</button>
								)}
							</div>

							{/* Search */}
							<form onSubmit={handleSearch} className="mb-5">
								<Label className="mb-2 block font-mono text-[9px] font-bold uppercase tracking-widest text-dashboard-muted">
									{t("common.search")}
								</Label>
								<Input
									type="text"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder={t("browse.searchDesigners")}
									className="rounded-xl border-dashboard-border bg-dashboard-bg text-dashboard-text font-body text-sm shadow-[var(--dashboard-shadow-1)] focus-visible:ring-dashboard-accent focus-visible:border-dashboard-accent"
								/>
							</form>

							{/* City */}
							<div className="mb-5">
								<Label className="mb-2 block font-mono text-[9px] font-bold uppercase tracking-widest text-dashboard-muted">
									{t("browse.city")}
								</Label>
								<Select
									value={selectedCity || "__all__"}
									onValueChange={(value) => {
										setSelectedCity(value === "__all__" ? "" : value);
										setPage(1);
									}}
								>
									<SelectTrigger className="w-full rounded-xl border-dashboard-border bg-dashboard-bg text-dashboard-text font-body text-sm shadow-[var(--dashboard-shadow-1)] focus:ring-dashboard-accent">
										<SelectValue placeholder="All Cities" />
									</SelectTrigger>
									<SelectContent className="border-dashboard-border bg-dashboard-paper">
										{CITIES.map((city) => (
											<SelectItem
												key={city.value || "__all__"}
												value={city.value || "__all__"}
												className="focus:bg-dashboard-surface focus:text-dashboard-text"
											>
												{city.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							{/* Budget */}
							<div className="mb-5">
								<Label className="mb-2 block font-mono text-[9px] font-bold uppercase tracking-widest text-dashboard-muted">
									{t("browse.budgetLabel")}
								</Label>
								<Select
									value={budgetMax || "__any__"}
									onValueChange={(value) => {
										setBudgetMax(value === "__any__" ? "" : value);
										setPage(1);
									}}
								>
									<SelectTrigger className="w-full rounded-xl border-dashboard-border bg-dashboard-bg text-dashboard-text font-body text-sm shadow-[var(--dashboard-shadow-1)] focus:ring-dashboard-accent">
										<SelectValue placeholder="Any Budget" />
									</SelectTrigger>
									<SelectContent className="border-dashboard-border bg-dashboard-paper">
										{BUDGET_RANGES.map((range) => (
											<SelectItem
												key={range.value || "__any__"}
												value={range.value || "__any__"}
												className="focus:bg-dashboard-surface focus:text-dashboard-text"
											>
												{range.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							{/* Design Styles */}
							<div>
								<Label className="mb-2.5 block font-mono text-[9px] font-bold uppercase tracking-widest text-dashboard-muted">
									{t("browse.designStyle")}
								</Label>
								<div className="flex flex-wrap gap-2">
									{DESIGN_STYLES.map((style) => {
										const isSelected = selectedStyles.includes(style);
										return (
											<ToggleChip
												key={style}
												selected={isSelected}
												onClick={() => {
													toggleStyle(style);
													setPage(1);
												}}
												className={cn(
													" x-3 py-1.5  text-xs rounded-full font-body transition-all duration-200 border",
													isSelected
														? "bg-dashboard-accent text-dashboard-on-fill border-dashboard-accent shadow-[var(--dashboard-shadow-1)]"
														: "bg-dashboard-bg border-dashboard-border text-dashboard-muted hover:bg-dashboard-surface hover:text-dashboard-text",
												)}
											>
												{style}
											</ToggleChip>
										);
									})}
								</div>
							</div>
						</div>
					</div>

					{/* Results */}
					<div className="flex-1">
						{/* Results count */}
						<div className="mb-5 flex items-center justify-between">
							<p className="font-mono text-[10px] font-bold uppercase tracking-widest text-dashboard-muted">
								{isLoading
									? t("common.loading")
									: t("common.xFound", {
											count: total,
											type: t("admin.designers").toLowerCase(),
										})}
							</p>
						</div>

						{/* Grid */}
						{isLoading ? (
							<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
								{[1, 2, 3, 4].map((i) => (
									<Skeleton
										key={i}
										className="h-52 rounded-2xl border border-dashboard-border/60 bg-dashboard-paper/40"
									/>
								))}
							</div>
						) : designers.length === 0 ? (
							<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-12 text-center shadow-[var(--dashboard-shadow-1)]">
								<p className="font-body text-dashboard-muted">
									{t("common.noResults")}
								</p>
								{hasFilters && (
									<button
										type="button"
										onClick={clearFilters}
										className="mt-4 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-accent-dark hover:underline"
									>
										{t("common.clearFilters")}
									</button>
								)}
							</div>
						) : (
							<>
								<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
									{designers.map((designer) => (
										<DesignerCard key={designer.id} designer={designer} />
									))}
								</div>

								{/* Pagination */}
								{totalPages > 1 && (
									<div className="mt-8 flex items-center justify-center gap-3">
										<button
											type="button"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={page === 1}
											className="rounded-xl border border-dashboard-border bg-dashboard-paper px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-text hover:bg-dashboard-surface disabled:opacity-50 shadow-[var(--dashboard-shadow-1)] transition-colors"
										>
											{t("common.previous")}
										</button>
										<span className="font-mono text-[11px] font-semibold text-dashboard-muted">
											{t("common.pageOf", { page, totalPages }).toUpperCase()}
										</span>
										<button
											type="button"
											onClick={() =>
												setPage((p) => Math.min(totalPages, p + 1))
											}
											disabled={page === totalPages}
											className="rounded-xl border border-dashboard-border bg-dashboard-paper px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-text hover:bg-dashboard-surface disabled:opacity-50 shadow-[var(--dashboard-shadow-1)] transition-colors"
										>
											{t("common.next")}
										</button>
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
